import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, DataTable, PrimaryButton, GhostButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg, formatDate } from "@/lib/api";

export default function Admins() {
  const { data, loading, reload } = useFetch("/admin/admins");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/admins", form); toast.success("Invitation emailed"); setOpen(false); setForm({ name: "", email: "" }); reload(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="admins-page">
      <PageHeader eyebrow="Superadmin" title="Admin accounts" description="Invite team members as admins. The seeded superadmin account is permanent and cannot be modified."
        actions={<PrimaryButton onClick={() => setOpen(true)} data-testid="invite-admin-button"><Plus className="h-4 w-4" />Invite admin</PrimaryButton>} />
      <DataTable testId="admins-table" loading={loading} rows={data} columns={[
        { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
        { key: "email", label: "Login" }, { key: "role", label: "Role" },
        { key: "status", label: "Status", render: (r) => (r.password_set ? r.status : "invite pending") },
        { key: "created_at", label: "Added", render: (r) => formatDate(r.created_at) },
        { key: "a", label: "", render: (r) => (r.is_protected ? <span className="inline-flex items-center gap-1 text-xs text-[#635060]"><Lock className="h-3 w-3" />Protected</span> : (
          <div className="flex gap-2">
            <GhostButton onClick={() => run(() => api.patch(`/admin/users/${r.id}/status`, { status: r.status === "disabled" ? "active" : "disabled" }), "Updated")} data-testid={`admin-toggle-${r.id}`}>{r.status === "disabled" ? "Enable" : "Disable"}</GhostButton>
            <GhostButton onClick={() => run(() => api.delete(`/admin/admins/${r.id}`), "Admin removed")} data-testid={`admin-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></GhostButton>
          </div>
        )) },
      ]} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-2xl sm:max-w-md" data-testid="invite-admin-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Invite an admin</DialogTitle><DialogDescription>They will receive a secure link to set their password.</DialogDescription></DialogHeader>
          <form onSubmit={invite} className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-admin-name-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-admin-email-input" /></div>
            <PrimaryButton type="submit" busy={busy} className="w-full" data-testid="invite-admin-submit">Send invitation</PrimaryButton>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
